/**
 * PDP photo-print configurator — Revelação de Fotos (Tradicional + Autocolante).
 *
 * Extracted verbatim in behaviour from the inline Liquid javascript block that
 * used to live in sections/product-fotos.liquid. Unlike the album PDP this is
 * plain DOM code, not a Preact island: the section renders every control
 * server-side and this module only binds to them through `data-*` hooks.
 *
 * It moved out of the section for the reason the album's did — an external
 * module is cached and bytecode-cached, an inline block is re-parsed on every
 * view — and for one the album did not have: Shopify scopes the compiled
 * section JS bundle to the sections a page actually renders (`<script
 * id="sections-script" data-sections="…">`). The cart drawer opens on the
 * collection and home pages, where `product-fotos` is not in that list, so the
 * inline block simply did not exist there and could never have edited a photo
 * line from the bag.
 *
 * TWO SURFACES, ONE MODULE.
 *   PDP    — importing this module binds every `.pf` section in the document,
 *            each reading its own `.pf-config-data` island. Unchanged behaviour.
 *   DRAWER — `assets/cart-edit-host.js` mounts the `fotos` surface against a
 *            config fetched from the line's own product URL, and this module
 *            builds a flat quantity / border / add-on form inside the panel.
 *
 * The two do not share state: `initConfigurator()` closes over one root, so a
 * panel opened on top of a photo PDP leaves that PDP's controls alone.
 *
 * Because the panel's form is built here rather than cloned from the PDP, every
 * label it shows comes through the config island's `strings` — the same
 * `section.settings.*` values the section renders — so the two surfaces cannot
 * drift apart in wording. The `.pf-*` classes it reuses are declared in the
 * section's own stylesheet block, which Shopify concatenates into the
 * theme-wide `styles.css` loaded on every page, so the panel is styled on a
 * collection page even though `pdp-shared.css` is not there.
 */

import { resolveInstallments } from './pdp-installments.js';

// Both are null under `node --test`, which is what lets this module be imported
// outside a browser: the auto-mount and the surface registration at the bottom
// are the only things that read the page, and both are guarded.
const pageWindow = typeof window === 'undefined' ? null : window;
const pageDocument = typeof document === 'undefined' ? null : document;

const BRL = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });
function money(cents) {
  return BRL.format((cents || 0) / 100);
}

// Relative luminance → choose a dark check on light swatches, white on dark.
function isLightHex(hex) {
  if (!hex) return false;
  let normalized = hex.replace('#', '');
  if (normalized.length === 3) {
    normalized = normalized[0] + normalized[0] + normalized[1] + normalized[1] + normalized[2] + normalized[2];
  }
  const red = parseInt(normalized.substr(0, 2), 16) / 255;
  const green = parseInt(normalized.substr(2, 2), 16) / 255;
  const blue = parseInt(normalized.substr(4, 2), 16) / 255;
  return (0.2126 * red + 0.7152 * green + 0.0722 * blue) > 0.7;
}

// ── Panel markup ────────────────────────────────────────────────────────────
// Built node by node rather than from an HTML string: every label below is
// merchant copy coming off the wire, and `textContent` is what keeps it copy
// instead of markup.
function createElement(ownerDocument, tag, attributes, children) {
  const node = ownerDocument.createElement(tag);
  Object.keys(attributes || {}).forEach((name) => {
    const value = attributes[name];
    if (value === null || value === undefined || value === false) return;
    if (name === 'class') node.className = value;
    else if (name === 'text') node.textContent = value;
    else node.setAttribute(name, value === true ? '' : value);
  });
  node.append(...(children || []).filter(Boolean));
  return node;
}

const SVG_NS = 'http://www.w3.org/2000/svg';

// The selected-swatch tick, identical to the one the section renders.
// createElementNS rather than an innerHTML string: an SVG child of an HTML
// element only gets the SVG namespace when it is created with one, and keeping
// the whole builder markup-free is what lets a test drive it.
function checkIcon(ownerDocument) {
  const svg = ownerDocument.createElementNS(SVG_NS, 'svg');
  svg.setAttribute('class', 'pf-swatch__check');
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('fill', 'none');
  svg.setAttribute('stroke', 'currentColor');
  svg.setAttribute('stroke-width', '3.5');
  svg.setAttribute('stroke-linecap', 'round');
  svg.setAttribute('stroke-linejoin', 'round');
  svg.setAttribute('aria-hidden', 'true');
  const path = ownerDocument.createElementNS(SVG_NS, 'path');
  path.setAttribute('d', 'M20 6 9 17l-5-5');
  svg.append(path);
  return svg;
}

/**
 * Render the drawer's flat edit form into `root`: quantity, border, add-on —
 * the same three fields the PDP shows, in the PDP's order.
 *
 * Deliberately NOT the whole buy box. Size is fixed for a line that already
 * exists (the drawer opens the product URL the line was added from, so the
 * island already describes that size), the package pills are a shortcut for
 * choosing a quantity the stepper can reach anyway, and price/recap belong to
 * the cart the panel slides back to.
 */
export function buildPanelForm(root, cfg) {
  // The document comes from the root the host handed us, not from a module
  // global: that is what lets a test drive this builder against a DOM double.
  const ownerDocument = root.ownerDocument;
  const make = (tag, attributes, children) => createElement(ownerDocument, tag, attributes, children);
  const strings = cfg.strings;
  const fields = [];

  fields.push(make('div', { class: 'pf-field' }, [
    make('div', { class: 'pf-field__labelrow' }, [
      make('span', { class: 'pf-field__label', text: strings.qtyLabel }),
      // Flat text where the section splits the number into its own `data-min-qty`
      // span: that span exists so the size selector can rewrite the minimum, and
      // the panel has no size selector.
      make('span', {
        class: 'pf-field__hint',
        text: [strings.qtyMinPrefix, cfg.minQty, strings.qtyMinSuffix].filter((part) => part || part === 0).join(' '),
      }),
    ]),
    make('div', { class: 'pf-stepper', 'data-stepper': true }, [
      make('button', {
        type: 'button', class: 'pf-stepper__btn', 'data-qty-dec': true, 'aria-label': strings.a11yDecrease, text: '−',
      }),
      make('input', {
        type: 'text',
        inputmode: 'numeric',
        pattern: '[0-9]*',
        class: 'pf-stepper__val',
        'data-qty-val': true,
        value: String(cfg.minQty),
        autocomplete: 'off',
        maxlength: '4',
        'aria-label': strings.qtyLabel,
      }),
      make('button', {
        type: 'button', class: 'pf-stepper__btn', 'data-qty-inc': true, 'aria-label': strings.a11yIncrease, text: '+',
      }),
    ]),
    make('p', { class: 'pf-visually-hidden', role: 'status', 'data-qty-announce': true }),
  ]));

  // Both optional fields keep the section's own `data-*-rule` / `data-*-field`
  // pair, because applyVariant() toggles exactly those two nodes and a panel
  // that omitted them would need a second rule for the same thing.
  if (cfg.showBorder) {
    fields.push(make('div', { class: 'pf-rule', 'data-border-rule': true }));
    fields.push(make('div', { class: 'pf-field', 'data-border-field': true }, [
      make('span', { class: 'pf-field__label', text: strings.borderLabel }),
      make('div', { class: 'pf-borda', 'data-borda': true }, [
        make('button', {
          type: 'button', class: 'pf-borda__btn is-selected', 'data-border': true, 'data-border-val': 'border', text: strings.borderWith,
        }),
        make('button', {
          type: 'button', class: 'pf-borda__btn', 'data-border': true, 'data-border-val': 'none', text: strings.borderWithout,
        }),
      ]),
    ]));
  }

  if (cfg.box) {
    const swatches = (cfg.boxColors || []).map((color, index) => make('button', {
      type: 'button',
      class: 'pf-swatch' + (index === 0 ? ' is-selected' : ''),
      'data-box-swatch': true,
      'data-index': String(index),
      'data-hex': color.hex,
      'data-label': color.label,
      role: 'radio',
      'aria-checked': index === 0 ? 'true' : 'false',
      'aria-label': color.label,
    }, [
      make('span', { class: 'pf-swatch__chip', style: '--swatch: ' + color.hex }),
      // A constant icon, so a markup string is safe here where the labels above
      // are not. updateColorReadout() recolours it per swatch luminance.
      checkIcon(ownerDocument),
    ]));

    fields.push(make('div', { class: 'pf-rule', 'data-box-rule': true }));
    fields.push(make('div', { class: 'pf-field', 'data-box-field': true }, [
      make('div', { class: 'pf-field__labelrow' }, [
        make('span', { class: 'pf-field__label', text: strings.addonLabel }),
      ]),
      make('div', { class: 'pf-addon', 'data-box': true }, [
        make('div', { class: 'pf-addon__head' }, [
          cfg.boxImage
            ? make('span', { class: 'pf-addon__thumb' }, [
              make('img', { src: cfg.boxImage, width: '52', height: '52', alt: cfg.box.name, loading: 'lazy' }),
            ])
            : null,
          make('span', { class: 'pf-addon__info' }, [
            make('span', { class: 'pf-addon__name', text: cfg.box.name }),
            make('span', { class: 'pf-addon__sub', text: strings.boxSub }),
          ]),
          make('button', { type: 'button', class: 'pf-addon__remove', 'data-box-remove': true, text: strings.boxRemove }),
        ]),
        make('button', { type: 'button', class: 'pf-addon__cta', 'data-box-toggle': true }),
        make('div', { class: 'pf-addon__panel' }, [
          make('div', { class: 'pf-addon__divider' }),
          make('span', { class: 'pf-addon__cor', text: strings.boxColorLabel }),
          make('div', {
            class: 'pf-swatches', role: 'radiogroup', 'aria-label': strings.boxColorLabel, 'data-box-swatches': true,
          }, swatches),
          make('div', { class: 'pf-swatches-scroll', 'aria-hidden': 'true' }, [
            make('span', { class: 'pf-swatches-scroll__thumb', 'data-box-scroll-thumb': true }),
          ]),
          make('div', { class: 'pf-readout' }, [
            make('span', { class: 'pf-readout__chip', 'data-box-readout-chip': true }),
            make('span', { 'data-box-readout': true }),
          ]),
        ]),
      ]),
    ]));
  }

  // The id the drawer's SAVE button points `aria-describedby` at, so a failed
  // save is announced with the button and not only in the scroll area above it.
  fields.push(make('p', { class: 'pf-add-error', id: 'cart-edit-error', 'data-atc-error': true, role: 'alert', hidden: true }));

  // `.pf` is the custom-property scope the add-on card and the dividers read
  // from, `.pf-config` the column that spaces the fields; `.pf--panel` only
  // drops the section's page padding, which means nothing inside a drawer pane.
  root.replaceChildren(make('div', { class: 'pf pf-config pf--panel' }, fields));
}

// ── Controller ──────────────────────────────────────────────────────────────
/**
 * Bind one photo configurator to one root.
 *
 * @param {Element} root
 * @param {object}  [options]
 * @param {object}  [options.config]  parsed island; read from `root` when absent
 * @param {string}  [options.editKey] cart line to hydrate from (drawer panel)
 * @param {boolean} [options.panel]   drawer panel rather than the PDP
 *
 * Resolves once the edit hydration has settled, so the drawer can keep its
 * loading state up until the fields actually show the line's values.
 */
async function initConfigurator(root, options) {
  const settings = options || {};
  const isPanel = !!settings.panel;

  if (!isPanel) {
    if (root.dataset.pfReady) return null;
    root.dataset.pfReady = '1';
  }

  const select = (selector) => root.querySelector(selector);
  const selectAll = (selector) => Array.prototype.slice.call(root.querySelectorAll(selector));

  // "Ver mais" popup: open by id + close via X / click-outside (same wiring as
  // the album PDP's popups; Esc is native <dialog> behaviour). The panel builds
  // no popups, so this binds nothing there.
  const closePopup = (dialog) => {
    if (typeof dialog.close === 'function') dialog.close();
    else dialog.removeAttribute('open');
  };
  selectAll('[data-popup-open]').forEach((opener) => {
    opener.addEventListener('click', () => {
      const dialog = pageDocument.getElementById(opener.getAttribute('data-popup-open'));
      if (!dialog) return;
      if (typeof dialog.showModal === 'function') dialog.showModal();
      else dialog.setAttribute('open', '');
    });
  });
  selectAll('.pf-popup').forEach((dialog) => {
    Array.prototype.forEach.call(dialog.querySelectorAll('[data-popup-close]'), (closer) => {
      closer.addEventListener('click', () => { closePopup(dialog); });
    });
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) closePopup(dialog);
    });
  });

  let cfg = settings.config || null;
  if (!cfg) {
    const dataEl = select('.pf-config-data');
    if (!dataEl) return null;
    cfg = JSON.parse(dataEl.textContent);
  }
  const strings = cfg.strings;

  if (isPanel) buildPanelForm(root, cfg);

  let qty = cfg.minQty;
  // maxlength caps the field; this caps the buttons and the edit-mode cart read,
  // which never pass through it. Math.min(9999, …) because max_qty is a "number"
  // setting with no schema-side max: 20000 in the editor must not let a button
  // commit 5 digits into a 4-digit field.
  const maxQty = Math.min(9999, Number(cfg.maxQty) || 9999);
  // `|| cfg.minQty` first absorbs NaN and 0 before either Math sees one —
  // Math.min(maxQty, NaN) is NaN and would reach the order. Floor after that,
  // never before: max_qty accepts decimals, so the fractional value can be the
  // ceiling OR the quantity, and only flooring the result catches both. The
  // minimum is applied LAST and wins: it is a per-size product rule, the
  // ceiling an operational setting, so max_qty = 30 must not commit a 5x6
  // line at 30.
  function clampQty(quantity) { return Math.max(cfg.minQty, Math.floor(Math.min(maxQty, quantity || cfg.minQty))); }
  let border = 'border';
  let boxOn = false;
  let colorIdx = 0;
  let editKey = settings.editKey || null;
  let carriedProps = {};
  // What a save that did not finish left behind, so the next click resumes it
  // instead of adding a second copy. See commitSave().
  let pendingSave = null;

  const els = {
    priceNow: select('[data-price-now]'),
    priceWas: select('[data-price-was]'),
    priceOff: select('[data-price-off]'),
    installment: select('[data-price-installment]'),
    pix: select('[data-price-pix]'),
    qtyVal: select('[data-qty-val]'),
    pkgs: selectAll('[data-pkg]'),
    recapDesc: select('[data-recap-desc]'),
    recapWas: select('[data-recap-was]'),
    recapTotal: select('[data-recap-total]'),
    box: select('[data-box]'),
    boxToggle: select('[data-box-toggle]'),
    boxReadout: select('[data-box-readout]'),
    boxReadoutChip: select('[data-box-readout-chip]'),
    swatches: selectAll('[data-box-swatch]'),
  };

  // Highest discount tier whose quantity threshold is met.
  function activeTier() {
    let best = null;
    (cfg.tiers || []).forEach((tier) => {
      if (qty >= tier.q && (!best || tier.q > best.q)) best = tier;
    });
    return best;
  }

  function selectedSwatch() {
    return els.swatches[colorIdx] || els.swatches[0] || null;
  }

  // keepFieldText is passed only by the typing handler: it is the one caller
  // whose re-render must not touch the field, because the shopper is mid-edit
  // and rewriting .value would reset the caret. Every other caller changed qty
  // itself and wants the field to follow — including while the field still has
  // focus, which is the normal state on Safari, where a button tap never blurs.
  //
  // The panel renders only a subset of these nodes; every write below is
  // null-guarded, which is what lets one render() serve both surfaces.
  function render(keepFieldText) {
    const tier = activeTier();
    const pct = tier ? tier.p : 0;
    const perPhoto = Math.round(cfg.basePrice * (1 - pct / 100));
    const photosTotal = perPhoto * qty;
    const boxTotal = (boxOn && cfg.box) ? cfg.box.price : 0;
    const total = photosTotal + boxTotal;
    const fullTotal = cfg.basePrice * qty + boxTotal;
    const hasDiscount = pct > 0;

    // Headline price
    if (els.priceNow) els.priceNow.textContent = money(total);
    if (els.priceWas) {
      els.priceWas.textContent = money(fullTotal);
      els.priceWas.hidden = !hasDiscount;
    }
    if (els.priceOff) {
      els.priceOff.textContent = '-' + pct + '%';
      els.priceOff.hidden = !hasDiscount;
    }
    // Bounds arrive pre-normalised by snippets/installments-rule.liquid, and the
    // resolver defaults them again for the island that is missing or zeroed.
    // Being a module is what lets this share the rule instead of restating it —
    // the inline block it replaced had no way to import.
    const installments = resolveInstallments(total, cfg.installmentsRule);
    if (els.installment) {
      els.installment.textContent = (strings.installmentsTemplate || '{count}× de {price} sem juros')
        .replace('{count}', installments.count)
        .replace('{price}', money(Math.round(installments.perInstallmentCents)));
    }
    if (els.pix) {
      const pixCents = Math.round(total * (1 - (Number(cfg.pixPercent) || 0) / 100));
      els.pix.textContent = (strings.pixTemplate || '{price} no Pix').replace('{price}', money(pixCents));
    }

    // Quantity + package highlight.
    if (els.qtyVal && !keepFieldText) els.qtyVal.value = qty;
    els.pkgs.forEach((packageButton) => {
      packageButton.classList.toggle('is-selected', !!tier && Number(packageButton.dataset.qty) === tier.q);
    });

    // Recap
    if (els.recapDesc) {
      // "120x Revelação de Fotos · Caixa Cristal · " (struck price follows inline)
      let desc = qty + 'x ' + strings.summaryPrefix;
      if (boxOn && cfg.box) desc += ' · ' + cfg.box.name;
      if (hasDiscount) desc += ' · ';
      els.recapDesc.textContent = desc;
    }
    if (els.recapWas) {
      els.recapWas.textContent = money(fullTotal);
      els.recapWas.hidden = !hasDiscount;
    }
    if (els.recapTotal) els.recapTotal.textContent = money(total);

    // Box card
    if (els.box) els.box.classList.toggle('is-open', boxOn);
    if (els.boxToggle && cfg.box) {
      els.boxToggle.textContent = strings.boxCtaPrefix + ' – ' + money(cfg.box.price);
    }
    if (boxOn && cfg.box) updateColorReadout();
    if (boxOn) updateSwatchScroll();
  }

  function updateColorReadout() {
    const swatch = selectedSwatch();
    if (!swatch) return;
    const hex = swatch.dataset.hex;
    const label = swatch.dataset.label;
    if (els.boxReadoutChip) els.boxReadoutChip.style.setProperty('--swatch', hex);
    if (els.boxReadout) els.boxReadout.textContent = label + ' – ' + money(cfg.box.price);
    const check = swatch.querySelector('.pf-swatch__check');
    if (check) check.style.color = isLightHex(hex) ? '#151616' : '#ffffff';
  }

  // Mobile colour row: always-visible 1px scroll indicator (thumb width + position).
  const swatchScroll = select('[data-box-swatches]');
  const swatchThumb = select('[data-box-scroll-thumb]');
  function updateSwatchScroll() {
    if (!swatchScroll || !swatchThumb) return;
    const scrollable = swatchScroll.scrollWidth > swatchScroll.clientWidth;
    const ratio = scrollable ? swatchScroll.clientWidth / swatchScroll.scrollWidth : 1;
    swatchThumb.style.width = (ratio * 100) + '%';
    swatchThumb.style.left = (scrollable ? (swatchScroll.scrollLeft / swatchScroll.scrollWidth) * 100 : 0) + '%';
  }

  function selectColor(index) {
    colorIdx = index;
    els.swatches.forEach((swatch, swatchIndex) => {
      const on = swatchIndex === index;
      swatch.classList.toggle('is-selected', on);
      swatch.setAttribute('aria-checked', on ? 'true' : 'false');
    });
    updateColorReadout();
  }

  // ── Tamanho (variant) selector — switches everything client-side, no reload ──
  // The server rendered the page for the size it opened on; picking a chip
  // re-points every size-dependent piece (price, minimum, border, box, the
  // freight quote inputs) at the chosen variant. Product-level things
  // (gallery, packages ladder, delivery-deadline banner) are identical
  // across sizes and stay put.
  //
  // The panel renders no size chips: the line already has a size, and the
  // drawer opened the line's own product URL, so the island it was configured
  // with is already that size's.
  const sizeButtons = selectAll('[data-size]');
  const variantsById = {};
  (cfg.variants || []).forEach((entry) => { variantsById[entry.id] = entry; });

  const sizeBadge = select('[data-size-badge]');
  const minQtyEl = select('[data-min-qty]');
  const borderRule = select('[data-border-rule]');
  const borderField = select('[data-border-field]');
  const boxRule = select('[data-box-rule]');
  const boxField = select('[data-box-field]');
  const prazoHost = root.querySelector('[data-pf-prazo]');

  // Re-point the freight estimator at the new size and drop any quote already
  // on screen — it was priced for the old size. The estimator re-reads its
  // config island on every calculate(), so patching the island is enough for
  // the next quote to be correct; the Expresso row's price is server-rendered
  // (10% of the variant price) so we refresh that node directly.
  function patchPrazo(entry) {
    if (!prazoHost) return;
    const island = prazoHost.querySelector('[data-prazo-config]');
    if (island) {
      try {
        const prazoCfg = JSON.parse(island.textContent);
        prazoCfg.variant = { id: entry.id };
        island.textContent = JSON.stringify(prazoCfg);
      } catch { /* leave the island untouched on parse failure */ }
    }
    const panel = prazoHost.querySelector('[data-prazo-panel]');
    if (panel) panel.hidden = true;
    const prazoError = prazoHost.querySelector('[data-prazo-error]');
    if (prazoError) prazoError.hidden = true;
    const expressoPriceEl = prazoHost.querySelector('[data-prazo-expresso-price]');
    if (expressoPriceEl && entry.expressoPrice) expressoPriceEl.textContent = entry.expressoPrice;
  }

  if (prazoHost) {
    prazoHost.addEventListener('prazo:request-lines', (event) => {
      event.detail.lines = buildItems('');
    });
  }

  function applyVariant(entry, isInitial) {
    if (!entry) return;
    cfg.variantId = entry.id;
    cfg.basePrice = entry.price;
    cfg.compareAt = entry.compareAt;
    cfg.minQty = entry.minQty;
    cfg.showBorder = entry.showBorder;
    cfg.sizeLabel = entry.label;
    cfg.box = entry.box;

    // The new size's rules apply in full, not just its floor (5x6 needs 45):
    // clampQty reads the cfg.minQty assigned just above, and a quantity typed
    // past the ceiling without a blur must not survive the switch either.
    const qtyBefore = qty;
    qty = clampQty(qty);
    // A size with no box can't keep a box selected.
    if (!cfg.box) boxOn = false;

    sizeButtons.forEach((button) => {
      const on = Number(button.dataset.variantId) === entry.id;
      button.classList.toggle('is-selected', on);
      button.setAttribute('aria-checked', on ? 'true' : 'false');
    });
    if (sizeBadge) sizeBadge.textContent = entry.label;
    if (minQtyEl) minQtyEl.textContent = entry.minQty;

    if (borderRule) borderRule.hidden = !entry.showBorder;
    if (borderField) borderField.hidden = !entry.showBorder;

    const showBox = !!entry.box;
    if (boxRule) boxRule.hidden = !showBox;
    if (boxField) boxField.hidden = !showBox;

    patchPrazo(entry);

    // Keep the URL on the chosen size so a refresh or shared link reopens it.
    // The panel never reaches this — it renders no size chips — which is what
    // keeps an edit inside the drawer from rewriting the collection page's URL.
    if (!isInitial && pageWindow.history && pageWindow.history.replaceState) {
      try {
        const url = new URL(pageWindow.location.href);
        url.searchParams.set('variant', entry.id);
        pageWindow.history.replaceState({}, '', url);
      } catch { /* older browsers: skip the URL sync, the switch still works */ }
    }

    render();
    // The switch can move qty on its own (new floor, or a ceiling the field
    // never re-validated). render() already put the number on screen; the input
    // has no aria-live, so only the announcement is left to do here.
    if (!isInitial && qty !== qtyBefore) announceQty();
  }

  sizeButtons.forEach((button) => {
    button.addEventListener('click', () => {
      applyVariant(variantsById[Number(button.dataset.variantId)], false);
    });
  });

  // ── Events ──
  const dec = select('[data-qty-dec]');
  const inc = select('[data-qty-inc]');

  // Announce button/package-driven quantity changes to screen readers via the
  // hidden status element. Typing is deliberately not announced here — the
  // screen reader already echoes the keystrokes, and render() runs per key.
  const announceEl = select('[data-qty-announce]');
  function announceQty() {
    if (!announceEl) return;
    const label = els.qtyVal ? els.qtyVal.getAttribute('aria-label') || '' : '';
    announceEl.textContent = (label ? label + ' ' : '') + qty;
  }

  // Safari does not blur a focused input on button tap, so `qty` can still be
  // behind what the field shows. The field is what the shopper is looking at,
  // so the step starts there — clamped first, which turns a typed 5 into 21
  // and an emptied field into 21 as well, both matching where a browser that
  // ran blur before the click would have landed. Stepping the raw `qty`
  // instead made + look dead after a 5, and jump to 81 after an empty field.
  function stepQty(delta) {
    const shown = els.qtyVal ? parseInt(els.qtyVal.value, 10) : qty;
    qty = clampQty(clampQty(shown) + delta);
    render();
    announceQty();
  }
  if (dec) dec.addEventListener('click', () => { stepQty(-1); });
  if (inc) inc.addEventListener('click', () => { stepQty(1); });

  // Typing the quantity. The minimum is only enforced on blur — clamping per
  // keystroke would turn the "1" of "100" into the minimum and swallow the rest.
  if (els.qtyVal) {
    els.qtyVal.addEventListener('input', () => {
      const digits = els.qtyVal.value.replace(/\D/g, '');
      if (digits !== els.qtyVal.value) {
        // Rewriting .value drops the caret to the end. Count the digits that
        // survived ahead of it and put it back there, so deleting a character
        // inside "1000" leaves the cursor where the shopper was working.
        const caret = els.qtyVal.selectionStart;
        const digitsBeforeCaret = els.qtyVal.value.slice(0, caret).replace(/\D/g, '').length;
        els.qtyVal.value = digits;
        els.qtyVal.setSelectionRange(digitsBeforeCaret, digitsBeforeCaret);
      }
      const typed = parseInt(digits, 10);
      if (typed > 0) {
        qty = typed;
        render(true);
      }
    });

    els.qtyVal.addEventListener('blur', () => {
      const typedValue = parseInt(els.qtyVal.value, 10);
      qty = clampQty(typedValue);
      render();
      // Announce only when the clamp CHANGED the value: a silent correction is
      // invisible to a screen-reader user, but re-announcing an unchanged number
      // the reader already echoed while typing is noise.
      if (qty !== typedValue) announceQty();
    });

    els.qtyVal.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        els.qtyVal.blur();
      }
    });
  }

  els.pkgs.forEach((packageButton) => {
    packageButton.addEventListener('click', () => {
      qty = clampQty(Number(packageButton.dataset.qty));
      render();
      announceQty();
    });
  });

  selectAll('[data-border]').forEach((button) => {
    button.addEventListener('click', () => {
      border = button.dataset.borderVal;
      selectAll('[data-border]').forEach((other) => { other.classList.toggle('is-selected', other === button); });
    });
  });

  if (els.boxToggle) {
    els.boxToggle.addEventListener('click', () => { boxOn = true; render(); });
  }
  const boxRemove = select('[data-box-remove]');
  if (boxRemove) boxRemove.addEventListener('click', () => { boxOn = false; render(); });

  els.swatches.forEach((swatch) => {
    swatch.addEventListener('click', () => { selectColor(Number(swatch.dataset.index)); });
  });

  if (swatchScroll) swatchScroll.addEventListener('scroll', updateSwatchScroll);
  // Owned rather than fire-and-forget: the panel is torn down and rebuilt on
  // every open, and a listener left on `window` would keep a detached root alive.
  pageWindow.addEventListener('resize', updateSwatchScroll);

  const atc = select('[data-atc]');
  const atcLabel = select('[data-atc-label]');
  const atcError = select('[data-atc-error]');
  let defaultAtcLabel = atcLabel ? atcLabel.textContent.trim() : '';
  let adding = false;

  function setAtcError(message) {
    if (!atcError) return;
    atcError.textContent = message || '';
    atcError.hidden = !message;
  }

  // ── The drawer's half of the protocol ─────────────────────────────────────
  // On the PDP these three are inert: the section owns a real submit button and
  // its own label/spinner. In the panel the button lives in the drawer's footer,
  // so the same state has to travel as events instead.
  function notifyState() {
    if (!isPanel) return;
    root.dispatchEvent(new CustomEvent('fn:edit-state', {
      detail: { busy: adding, canSave: !adding && !!cfg.variantId && !!editKey, error: atcError && !atcError.hidden ? atcError.textContent : '' },
    }));
  }

  /** A fresh group id. Ties the photo line and its add-on into one order. */
  function newGroupId() {
    return (pageWindow.crypto && pageWindow.crypto.randomUUID)
      ? pageWindow.crypto.randomUUID()
      : ('g' + Date.now() + Math.round(Math.random() * 1e6));
  }

  /** An Error that also carries the attempt a retry should resume from. */
  function saveError(message, pending) {
    const error = new Error(message || strings.addError || '');
    error.pending = pending || null;
    return error;
  }

  /** POST JSON and report status and body together, without throwing on 4xx. */
  async function postJson(url, body) {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await response.json().catch(() => null);
    return { ok: response.ok, data };
  }

  /** The line items this save would post, under `group`. */
  function buildItems(group) {
    const photoProps = { ...carriedProps, _group: group, 'Tamanho': cfg.sizeLabel, 'Quantidade': String(qty) };
    if (cfg.showBorder) {
      photoProps['Borda'] = (border === 'border') ? strings.borderWith : strings.borderWithout;
    }
    const built = [{ id: cfg.variantId, quantity: qty, properties: photoProps }];
    if (boxOn && cfg.box && cfg.box.variantId) {
      const swatch = els.swatches[colorIdx];
      built.push({
        id: cfg.box.variantId,
        quantity: 1,
        properties: { _group: group, _addon: 'caixa_cristal', 'Cor da caixa': swatch ? swatch.getAttribute('data-label') : '' },
      });
    }
    return built;
  }

  /**
   * Put the configured photos in the bag — replacing the edited group when there
   * is one. Returns the rendered `cart-drawer` section.
   *
   * ── Why the replacement goes in FIRST ──────────────────────────────────────
   * Because the failure that matters is losing the buyer's work, and only this
   * order cannot cause it. If the add fails — the Caixa Cristal sold out, the
   * network dropped — nothing has been removed and the bag still holds what they
   * had.
   *
   * This reverses the order the inline configurator used, on a premise that is
   * false: that Shopify cannot target one of two line items sharing a variant,
   * so the replacement had to be added into an empty space. A line key is
   * `variantId:hash(properties)` and every save mints a fresh `_group`, so the
   * old line and its replacement never share a key and never merge. The picture
   * frame verified this against the store (see assets/pdp-porta-retrato.js).
   *
   * The removal is ONE `/cart/update.js` call. A per-line loop can fail halfway
   * and orphan the caixa cristal around a photo line that is already gone.
   *
   * The one failure left — added, then the removal fell over — leaves both
   * versions in the bag. That is visible and the buyer can delete one, which is
   * why it is the failure worth having.
   */
  async function commitSave() {
    const sectionsUrl = pageWindow.location.pathname;
    if (!editKey) {
      const added = await postJson(cfg.routes.cartAdd, {
        items: buildItems(newGroupId()), sections: 'cart-drawer', sections_url: sectionsUrl,
      });
      if (!added.ok) throw saveError(added.data && (added.data.description || added.data.message), null);
      return added.data && added.data.sections;
    }

    // The idempotency key identifies the PAYLOAD, not the attempt. The Cart AJAX
    // API offers none, so the group uuid stands in — but only while the buyer has
    // not changed anything: the panel stays editable behind a failed save, and
    // reusing the id after an edit would skip the add and leave the bag holding a
    // version they never confirmed.
    const signature = JSON.stringify(buildItems(''));
    const sameAsAttempt = !!pendingSave && pendingSave.signature === signature;
    const group = sameAsAttempt ? pendingSave.groupId : newGroupId();
    // What is being replaced is tracked as GROUP IDS, never as line keys.
    //
    // A line key is `variantId:hash(properties + discount allocation)`, and this
    // product sells under a quantity ladder — so adding the replacement changes
    // the total quantity of the variant, moves the OLD line into a different
    // tier, and re-keys it. Verified against the store: across one add, the
    // photo line's key changed while its caixa cristal add-on's (no ladder) did
    // not. Keys captured before the add are therefore stale by the time the
    // removal runs, and zeroing them silently removes the wrong line.
    //
    // `_group` is a plain property the theme writes itself, so it survives the
    // re-key and identifies the same lines afterwards.
    let oldGroups = pendingSave ? pendingSave.oldGroups : [];
    // Only for a legacy line carrying no `_group` at all: nothing else can name
    // it, and such a line has no add-on beside it to be orphaned from.
    let oldKeys = pendingSave ? pendingSave.oldKeys : [];
    let replacementIn = !!(sameAsAttempt && pendingSave.replacementIn);
    let sections = null;
    const attempt = () => ({ groupId: group, oldGroups, oldKeys, signature, replacementIn });

    if (!replacementIn) {
      const cart = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
        .then((response) => (response.ok ? response.json() : null))
        .catch(() => null);
      // A retry that cannot see the bag cannot tell whether its own previous add
      // landed. Adding blind is exactly how two replacements happen.
      if (!cart && pendingSave) throw saveError(strings.addError, pendingSave);
      if (cart) {
        const cartItems = cart.items || [];
        const inGroup = (id) => cartItems.some((item) => (item.properties || {})._group === id);
        // A previous attempt that no longer matches what the buyer wants left a
        // group behind nobody asked for. It goes out with the old one.
        const staleGroups = pendingSave && !sameAsAttempt && inGroup(pendingSave.groupId) ? [pendingSave.groupId] : [];

        if (sameAsAttempt && inGroup(group)) {
          replacementIn = true;
        } else {
          const current = cartItems.find((item) => item.key === editKey);
          if (current) {
            const currentGroup = (current.properties || {})._group;
            oldGroups = (currentGroup ? [currentGroup] : []).concat(staleGroups.filter((id) => id !== currentGroup));
            oldKeys = currentGroup ? [] : [current.key];
          } else if (staleGroups.length) {
            // The line this panel opened on is gone, but the abandoned attempt's
            // group is in the bag — that group is what this save now replaces,
            // rather than being left behind forever.
            oldGroups = staleGroups;
            oldKeys = [];
          } else {
            // Nothing of this order is left: it was removed somewhere else while
            // the panel was open, and adding would resurrect what they deleted.
            throw saveError(strings.lineGone, null);
          }
        }
      }
    }

    if (!replacementIn) {
      let added;
      try {
        added = await postJson(cfg.routes.cartAdd, {
          items: buildItems(group), sections: 'cart-drawer', sections_url: sectionsUrl,
        });
      } catch {
        // The POST may well have reached Shopify before this threw. Keeping the
        // group id is what lets the retry go and find out, instead of minting a
        // new one and adding a second copy.
        throw saveError(strings.addError, attempt());
      }
      // Nothing was removed, so the bag still holds exactly what the buyer had.
      if (!added.ok) {
        throw saveError((added.data && (added.data.description || added.data.message)) || strings.addError, attempt());
      }
      sections = (added.data && added.data.sections) || sections;
      replacementIn = true;
    }

    // Resolve the keys NOW, after the add, because the add is what invalidates
    // them (see `oldGroups` above). This read is the difference between removing
    // the old order and removing the one just created.
    const updates = {};
    oldKeys.forEach((oldKey) => { updates[oldKey] = 0; });
    if (oldGroups.length) {
      const bag = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
        .then((response) => (response.ok ? response.json() : null))
        .catch(() => null);
      // Both versions are in the bag and there is no safe way to tell which keys
      // to zero. Better to leave the buyer with a duplicate they can see and
      // delete than to guess and remove the wrong one.
      if (!bag) throw saveError(strings.oldCopyLeft, { ...attempt(), sections });
      (bag.items || []).forEach((item) => {
        const itemGroup = (item.properties || {})._group;
        if (itemGroup && itemGroup !== group && oldGroups.indexOf(itemGroup) > -1) updates[item.key] = 0;
      });
    }
    // The old group is already gone — removed elsewhere, or by a retry that got
    // further than it recorded. Nothing to do, and an empty update is a no-op.
    if (!Object.keys(updates).length) return sections;

    const removed = await postJson(cfg.routes.cartUpdate, {
      updates, sections: 'cart-drawer', sections_url: sectionsUrl,
    });
    if (!removed.ok) {
      // Both versions are in the bag. `sections` rides along so the bag can be
      // re-rendered: the message tells the buyer to delete the old copy, and that
      // advice is only actionable once they can actually see it.
      throw saveError(strings.oldCopyLeft, { ...attempt(), sections });
    }
    return (removed.data && removed.data.sections) || sections;
  }

  /**
   * Add the configured photo line — and, in edit mode, replace the old group.
   *
   * One path for both surfaces: the PDP's button and the drawer's SAVE run
   * exactly this, so the tier grid, the property names and the group bookkeeping
   * cannot drift between them.
   */
  async function submit() {
    if (adding || !cfg.variantId) return;
    // Last chance to catch a typed value the blur never saw (Safari does not
    // blur on button tap). Reading `qty` here would miss it: an emptied field
    // never touches `qty` (the input handler only commits digits > 0), so a
    // cleared "80" would resend 80 instead of falling back to the minimum —
    // read the field itself, same as stepQty does.
    const qtyBefore = qty;
    const shown = els.qtyVal ? parseInt(els.qtyVal.value, 10) : qty;
    qty = clampQty(shown);
    if (qty !== qtyBefore) {
      render();
      announceQty();
    }
    adding = true;
    setAtcError('');
    if (atc) atc.disabled = true;
    if (atcLabel) atcLabel.textContent = strings.adding;
    notifyState();

    try {
      const sections = await commitSave();
      pendingSave = null;
      // Inside the panel the drawer is already open and owns what happens next:
      // it re-renders the bag from these sections, slides back and toasts. On the
      // PDP the same payload is what opens the drawer in the first place.
      if (isPanel) root.dispatchEvent(new CustomEvent('fn:edit-saved', { detail: { sections } }));
      else pageDocument.dispatchEvent(new CustomEvent('cart:open', { detail: { sections } }));
    } catch (error) {
      pendingSave = (error && error.pending) || null;
      setAtcError((error && error.message) || strings.addError);
      // A half-finished save changed the bag; showing it is what makes the
      // message ("remove the old copy") something the buyer can act on.
      if (pendingSave && pendingSave.sections) {
        pageDocument.dispatchEvent(new CustomEvent('cart:open', { detail: { sections: pendingSave.sections } }));
      }
    } finally {
      adding = false;
      if (atc) atc.disabled = false;
      if (atcLabel) atcLabel.textContent = defaultAtcLabel;
      notifyState();
    }
  }

  if (atc) atc.addEventListener('click', submit);
  const onEditSave = () => { submit(); };
  if (isPanel) root.addEventListener('fn:edit-save', onEditSave);

  const detailsBar = select('[data-details-bar]');
  const detailsBody = select('[data-details-body]');
  const detailsIcon = select('[data-details-icon] svg path');
  if (detailsBar) {
    detailsBar.addEventListener('click', () => {
      const open = detailsBar.getAttribute('aria-expanded') === 'true';
      detailsBar.setAttribute('aria-expanded', open ? 'false' : 'true');
      if (detailsBody) detailsBody.hidden = open;
      if (detailsIcon) detailsIcon.setAttribute('d', open ? 'M12 5v14M5 12h14' : 'M5 12h14');
    });
  }

  // Gallery thumbnails
  const gallery = root.querySelector('[data-pf-gallery]');
  if (gallery) {
    gallery.addEventListener('click', (event) => {
      const thumb = event.target.closest('.pf-gallery__thumb');
      if (!thumb) return;
      const index = thumb.getAttribute('data-thumb');
      gallery.querySelectorAll('.pf-gallery__thumb').forEach((other) => {
        other.classList.toggle('is-active', other === thumb);
      });
      gallery.querySelectorAll('.pf-gallery__slide').forEach((slide) => {
        slide.classList.toggle('is-active', slide.getAttribute('data-slide') === index);
      });
    });
  }

  if (els.swatches.length) selectColor(0);

  /**
   * Edit mode: hydrate quantity / border / box from a cart line and its
   * same-group caixa_cristal add-on.
   *
   * The PDP reaches this through `?edit=<line_key>` on the URL (the no-JS path
   * the drawer link still falls back to); the panel is handed the key. Missing
   * or stale line → the PDP re-shows its estimator and stays a normal buy box,
   * and the panel simply cannot be saved.
   */
  async function hydrateEdit(key) {
    if (!key) return;
    // The estimator is a PDP-only node and belongs to the page, not to this
    // root, which is why it is looked up on the document.
    const estimator = isPanel ? null : pageDocument.querySelector('[data-pf-prazo]');
    if (estimator) estimator.hidden = true;
    try {
      const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      const cart = await response.json();
      const items = cart.items || [];
      const line = items.find((item) => item.key === key);
      if (!line) {
        if (estimator) estimator.hidden = false;
        editKey = null;
        return;
      }
      const props = line.properties || {};
      const group = props._group;
      carriedProps = {};
      Object.keys(props).forEach((name) => {
        if (name.charAt(0) === '_' && name !== '_group') carriedProps[name] = props[name];
      });
      const parsedQty = parseInt(props['Quantidade'], 10);
      if (parsedQty) qty = clampQty(parsedQty);
      if (cfg.showBorder && props['Borda']) {
        border = (props['Borda'] === strings.borderWith) ? 'border' : 'none';
        selectAll('[data-border]').forEach((button) => { button.classList.toggle('is-selected', button.dataset.borderVal === border); });
      }
      items.forEach((item) => {
        const addonProps = item.properties || {};
        if (addonProps._group !== group || addonProps._addon !== 'caixa_cristal') return;
        boxOn = true;
        const label = addonProps['Cor da caixa'];
        if (label) {
          els.swatches.forEach((swatch, index) => {
            if (swatch.getAttribute('data-label') === label) colorIdx = index;
          });
        }
      });
      editKey = key;
      if (atcLabel) { atcLabel.textContent = strings.saveChanges; defaultAtcLabel = strings.saveChanges; }
      if (els.swatches.length) selectColor(colorIdx);
      render();
    } catch {
      // graceful fallback: normal view
      if (estimator) estimator.hidden = false;
      editKey = null;
    }
  }

  render();

  const urlKey = isPanel || !pageWindow.location
    ? null
    : new URLSearchParams(pageWindow.location.search).get('edit');
  await hydrateEdit(isPanel ? editKey : urlKey);
  notifyState();

  return {
    destroy() {
      pageWindow.removeEventListener('resize', updateSwatchScroll);
      if (isPanel) {
        root.removeEventListener('fn:edit-save', onEditSave);
        root.replaceChildren();
      }
    },
  };
}

// ── Drawer panel surface ────────────────────────────────────────────────────
// `configure()` then `mount()` mirrors the album module's shape so the two
// surfaces are wired the same way from the host's side. Only the panel goes
// through them: the PDP binds its own sections directly below, so opening the
// panel over a photo PDP cannot disturb the page behind it.
let pendingConfig = null;
let activePanel = null;

export function configure(rawConfig) {
  pendingConfig = rawConfig;
}

export async function mount({ root, editKey }) {
  if (!pendingConfig) throw new Error('pdp-fotos: configure() before mount()');
  if (activePanel) unmount();
  activePanel = await initConfigurator(root, { config: pendingConfig, editKey, panel: true });
  return activePanel;
}

export function unmount() {
  if (!activePanel) return;
  activePanel.destroy();
  activePanel = null;
}

// ── PDP auto-mount ──────────────────────────────────────────────────────────
// Unchanged behaviour: importing this module binds every `.pf` section the
// document carries, each from its own island.
if (pageDocument) {
  const boot = () => { pageDocument.querySelectorAll('.pf').forEach((section) => { initConfigurator(section); }); };
  if (pageDocument.readyState === 'loading') pageDocument.addEventListener('DOMContentLoaded', boot);
  else boot();
}

// ── Cart-drawer edit surface ────────────────────────────────────────────────
// Claim the `fotos` surface so the drawer edits a photo line in place instead of
// navigating to `?edit=`. Registration is order-tolerant for the same reason the
// album's is: the host lazy-imports this module on click, but a photo PDP's own
// eager tag can run first, and module execution order between two script tags is
// not something to rely on.
function registerFotosSurface(cartEdit) {
  cartEdit.register('fotos', async ({ root, editKey, productUrl }) => {
    const productDocument = await cartEdit.loadProductDocument(productUrl);
    const island = productDocument.querySelector('.pf-config-data');
    if (!island) throw new Error('pdp-fotos: product page carries no .pf-config-data');
    // The line's URL carries its own `?variant=`, so the island the drawer just
    // fetched already describes that line's size — which is why the panel needs
    // no size selector to be correct about minimum, border and box.
    configure(JSON.parse(island.textContent));
    await mount({ root, editKey });
    return { destroy() { unmount(); } };
  });
}

if (pageWindow && pageWindow.fnCartEdit) {
  registerFotosSurface(pageWindow.fnCartEdit);
} else if (pageDocument) {
  pageDocument.addEventListener('fn:cart-edit-ready', () => registerFotosSurface(pageWindow.fnCartEdit), { once: true });
}
